import { useMemo } from "react";
import { ChevronRight, Home } from "lucide-react";
import { Link } from "react-router-dom";
import { useFolders, type Folder } from "@/hooks/useFolders";

interface FolderBreadcrumbProps {
  currentFolderId: string | null;
  className?: string;
}

export function FolderBreadcrumb({ currentFolderId, className }: FolderBreadcrumbProps) {
  const { folders } = useFolders();

  // Build path from root to current folder
  const breadcrumbPath = useMemo(() => {
    if (!currentFolderId) return [];

    const path: Folder[] = [];
    let currentId: string | null = currentFolderId;

    // Traverse up the tree
    while (currentId) {
      const folder = folders.find((f) => f.id === currentId);
      if (folder) {
        path.unshift(folder);
        currentId = folder.parent_id;
      } else {
        break;
      }
    }

    return path;
  }, [currentFolderId, folders]);

  if (!currentFolderId || breadcrumbPath.length === 0) {
    return null;
  }

  return (
    <nav className={className} aria-label="Breadcrumb">
      <ol className="flex items-center gap-1 text-sm text-muted-foreground flex-wrap">
        {/* Home / Documents */}
        <li>
          <Link
            to="/documents"
            className="flex items-center gap-1 hover:text-foreground transition-colors"
          >
            <Home className="w-3.5 h-3.5" />
            <span className="hidden sm:inline">Documents</span>
          </Link>
        </li>

        {/* Folder path */}
        {breadcrumbPath.map((folder, index) => {
          const isLast = index === breadcrumbPath.length - 1;

          return (
            <li key={folder.id} className="flex items-center gap-1">
              <ChevronRight className="w-3 h-3 text-muted-foreground/50" />
              {isLast ? (
                <span className="text-foreground font-medium truncate max-w-[150px]">
                  {folder.name}
                </span>
              ) : (
                <Link
                  to={`/documents?folder=${folder.id}`}
                  className="hover:text-foreground transition-colors truncate max-w-[120px]"
                >
                  {folder.name}
                </Link>
              )}
            </li>
          );
        })}
      </ol>
    </nav>
  );
}
